import { ChangeDetectionStrategy, Component, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SnackbarMessage, UIService } from '../ui.service';

interface ViewItem {
    msg: SnackbarMessage;
    leaving: boolean;
}

const EXIT_ANIMATION_MS = 250;

@Component({
    selector: 'app-snackbar-host',
    imports: [],
    templateUrl: './snackbar-host.component.html',
    styleUrls: ['./snackbar-host.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnackbarHostComponent {
    private readonly uiService = inject(UIService);
    private readonly platformId = inject(PLATFORM_ID);
    readonly isBrowser = isPlatformBrowser(this.platformId);

    private readonly viewItems = signal<ViewItem[]>([]);

    readonly bottomItems = computed(() =>
        this.viewItems().filter(v => v.msg.position === 'bottom-center'),
    );

    readonly topRightItems = computed(() =>
        this.viewItems().filter(v => v.msg.position === 'top-right'),
    );

    constructor() {
        if (!this.isBrowser) {
            return;
        }

        let prevIds = new Set<number>();

        effect(() => {
            const current = this.uiService.messages();
            const currentIds = new Set(current.map(m => m.id));

            const added = current.filter(m => !prevIds.has(m.id));
            const removed = [...prevIds].filter(id => !currentIds.has(id));

            if (added.length > 0) {
                this.viewItems.update(list => [
                    ...list,
                    ...added.map(msg => ({ msg, leaving: false })),
                ]);
            }

            for (const id of removed) {
                this.viewItems.update(list =>
                    list.map(v => (v.msg.id === id ? { ...v, leaving: true } : v)),
                );
                setTimeout(() => {
                    this.viewItems.update(list => list.filter(v => v.msg.id !== id));
                }, EXIT_ANIMATION_MS);
            }

            prevIds = currentIds;
        });
    }

    onDismiss(id: number): void {
        this.uiService.dismiss(id);
    }
}
